import {type DataLoader, useLoader} from "./DataLoader";
import type {FormEnvironment} from "../../Types";
import {computed, type ComputedRef, ref, watch} from "vue";
import type {AgendaData} from "@t/agenda/Agenda";
import type {View} from "@t/agenda/View";
import type {AgendaPart} from "@t/fd/layout/Agenda";
import {getter} from "../../Part";
import {findColumn} from "@fd/containers/agenda/AgendaUtils";

export function useAgendaLoader(env: FormEnvironment, ag:AgendaPart, localData: ComputedRef<FormData>, globalData: FormData):DataLoader {
    const loader = useLoader(ag);
    const loadedPage = ref<any[]>([])
    const loadedTotal = ref<number>(0);
    const data = computed<any[]>(() => loadedPage.value);
    const total = computed<number>(() => loadedTotal.value);

    const endpointUrl = computed<string>(() => {
        let url = ag.url;
        if (!url) throw "Missing url in agenda part";
        // Standard params for agenda protocol
        url = env.net.urlParam(url, "position", loader.position.value);
        if (loader.page.value > 0) url = env.net.urlParam(url, "size", loader.page.value);
        if (ag.method !== "QUERY") {
            // Sort must be specified in query parameter
            if (Array.isArray(loader.view.sort) && loader.view.sort.length > 0) {
                const colName = loader.view.sort[0].column;
                const desc = loader.view.sort[0].desc;
                const col = findColumn(colName, ag.columns);
                if (col) {
                    const obParam = col.fullName || col.name;
                    url = env.net.urlParam(url, "orderBy", obParam);
                    if (desc) url = env.net.urlParam(url, "desc", "true");
                }
            }
        }

        if (Array.isArray(ag.params)) {
            for (let par of ag.params) {
                let v;
                if (par.source === "static") v = par.value;
                else {
                    // from data
                    v = getter(localData.value, par.d, globalData);
                }
                if (par.path) {
                    url = url.replace("{" + par.name + "}", v);
                } else {
                    url = env.net.urlParam(url, par.name, v);
                }
            }
        }
        return url;
    });

    const filteringBody = computed<View|undefined>(() => {
        if (ag.method && ag.method.toUpperCase() !== "GET") {
            return loader.view;
        }
        return undefined;
    });

    function updateNow() {
        const endpoint = endpointUrl.value;
        const body = filteringBody.value;
        let method = ag.method;
        if (!method) method = "GET";
        else method = method.toUpperCase();
        loader.state.value = "loading";
        env.net.fetch(endpoint, method, body, "application/json")
            .then((resp) => {
                if (resp.ok) return resp.json();
                else throw "Error status "+resp.status;
            })
            .then((data:AgendaData<any>) => {
                loadedPage.value.length = 0;
                for (let item of data.items) {
                    loadedPage.value.push(item);
                }
                loadedTotal.value = data.total;
                loader.state.value = "loaded";
            })
            .catch((reason) => {
                console.error(reason);
                loader.state.value = "error";
                loader.errorMessage.value = reason.toString();
            });
    }

    let updateTimeout = 0;

    function update():void {
        if (!updateTimeout && !loader.paused.value) {
            updateTimeout = setTimeout(() => {
                updateTimeout = 0;
                updateNow();
            }, 50);
        }
    }

    watch([loader.position, loader.page, loader.paused], () => update());
    watch(() => loader.view, () => update(), { deep:true, immediate:true })

    return {
        ...loader,
        data,
        total
    };
}
